import React, { useState, useEffect } from 'react';
import { getFirestore, addDoc, collection } from 'firebase/firestore';
import Container from '../../common/Container';
import { Heading } from '../../common/TextComponents';
import Button from '../../common/Button';

const EmployerCTAFormSection = ({ isDarkMode = false, initialSearchQuery = '' }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    searchQuery: '',
  });
  
  const [formStatus, setFormStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  });

  // Pre-populate the search query from the hero section
  useEffect(() => {
    setFormData(prev => ({ ...prev, searchQuery: initialSearchQuery }));
  }, [initialSearchQuery]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData({
      ...formData,
      [id]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ submitting: true, success: false, error: null });
    
    try {
      const db = getFirestore();
      const submissionData = {
        userInfo: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          searchQuery: formData.searchQuery,
        },
        searchQuery: formData.searchQuery,
        searchTimestamp: new Date().toISOString(),
        completedAt: new Date().toISOString(),
        source: 'employer_cta_form_submission',
        status: 'completed',
      };
      
      await addDoc(collection(db, "employer_request"), submissionData);
      
      setFormStatus({ submitting: false, success: true, error: null });
      
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        searchQuery: initialSearchQuery, // Reset to initial query after submission
      });
      
      setTimeout(() => {
        setFormStatus(prev => ({ ...prev, success: false }));
      }, 5000);
    } catch (error) {
      console.error("Error submitting form:", error);
      setFormStatus({ 
        submitting: false, 
        success: false, 
        error: "There was an error submitting your request. Please try again.",
      });
    }
  };

  return (
    <div 
      id="employer-cta-form" 
      className={`py-16 md:py-24 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}
    >
      <Container>
        <div className="mx-auto max-w-2xl text-center">
          <Heading as="h2" dark={isDarkMode} className="mb-4">
            Ready to Connect with Talent That Thinks Differently?
          </Heading>
          <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Fill out the form below to get started. Our team will reach out to discuss your hiring needs and introduce you to candidates who match your requirements.
          </p>
        </div>

        <div className="mx-auto mt-12 max-w-xl">
          <div className={`rounded-2xl shadow-lg overflow-hidden ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
            <div className="p-6 sm:p-8">
              {formStatus.success && (
                <div className={`mb-6 rounded-md p-4 ${isDarkMode ? 'bg-green-900/20' : 'bg-green-50'}`}>
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className={`h-5 w-5 ${isDarkMode ? 'text-green-400' : 'text-green-500'}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className={`text-sm font-medium ${isDarkMode ? 'text-green-300' : 'text-green-800'}`}>Success!</h3>
                      <div className={`mt-2 text-sm ${isDarkMode ? 'text-green-200' : 'text-green-700'}`}>
                        <p>Your request has been submitted. We'll be in touch shortly.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {formStatus.error && (
                <div className={`mb-6 rounded-md p-4 ${isDarkMode ? 'bg-red-900/20' : 'bg-red-50'}`}>
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className={`h-5 w-5 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className={`text-sm font-medium ${isDarkMode ? 'text-red-300' : 'text-red-800'}`}>Error</h3>
                      <div className={`mt-2 text-sm ${isDarkMode ? 'text-red-200' : 'text-red-700'}`}>
                        <p>{formStatus.error}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="firstName" className={`block text-sm font-semibold leading-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      First Name
                    </label>
                    <div className="mt-2.5">
                      <input
                        type="text"
                        id="firstName"
                        className={`block w-full rounded-xl border-0 px-3.5 py-2.5 shadow-sm ring-1 ring-inset ${isDarkMode ? 'ring-gray-700 bg-gray-800 text-white placeholder:text-gray-400' : 'ring-gray-300 placeholder:text-gray-400'} focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm`}
                        placeholder="Your first name"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="lastName" className={`block text-sm font-semibold leading-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      Last Name
                    </label>
                    <div className="mt-2.5">
                      <input
                        type="text"
                        id="lastName"
                        className={`block w-full rounded-xl border-0 px-3.5 py-2.5 shadow-sm ring-1 ring-inset ${isDarkMode ? 'ring-gray-700 bg-gray-800 text-white placeholder:text-gray-400' : 'ring-gray-300 placeholder:text-gray-400'} focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm`}
                        placeholder="Your last name"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label htmlFor="email" className={`block text-sm font-semibold leading-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Work Email
                  </label>
                  <div className="mt-2.5">
                    <input
                      type="email"
                      id="email"
                      className={`block w-full rounded-xl border-0 px-3.5 py-2.5 shadow-sm ring-1 ring-inset ${isDarkMode ? 'ring-gray-700 bg-gray-800 text-white placeholder:text-gray-400' : 'ring-gray-300 placeholder:text-gray-400'} focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm`}
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="searchQuery" className={`block text-sm font-semibold leading-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    What you're looking for
                  </label>
                  <div className="mt-2.5">
                    <textarea
                      id="searchQuery"
                      rows={4}
                      className={`block w-full rounded-xl border-0 px-3.5 py-2.5 shadow-sm ring-1 ring-inset ${isDarkMode ? 'ring-gray-700 bg-gray-800 text-white placeholder:text-gray-400' : 'ring-gray-300 placeholder:text-gray-400'} focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm`}
                      placeholder="e.g., Senior Engineer, Marketing Director"
                      value={formData.searchQuery}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div>
                  <button
                    type="submit"
                    disabled={formStatus.submitting}
                    className={`group relative w-full rounded-xl px-6 py-4 text-base font-semibold text-white shadow-lg transition-all duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 ${
                      formStatus.submitting 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]'
                    }`}
                  >
                    {formStatus.submitting ? (
                      <div className="flex items-center justify-center gap-3">
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Submitting...
                      </div>
                    ) : (
                      'Request Access'
                    )}
                  </button>
                </div>
                <p className={`text-xs text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  By submitting this form, you agree to our <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-purple-500">Privacy Policy</a> and <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-purple-500">Terms of Service</a>.
                </p>
              </form>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default EmployerCTAFormSection;
